/**
 * Tamaño destino manteniendo proporción: reduce hasta que el lado mayor sea `maxSide`.
 * Nunca agranda; siempre devuelve enteros >= 1.
 */
export function computeTargetSize(
  width: number,
  height: number,
  maxSide = 1600,
): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= maxSide) {
    return { width: Math.round(width), height: Math.round(height) };
  }
  const ratio = maxSide / longest;
  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
  };
}

/** Resultado de comprimir, diciendo si de verdad se recodificó a JPEG. */
export interface CompressResult {
  blob: Blob;
  /**
   * `true` solo si el blob que se devuelve salió del canvas, o sea que **es** un JPEG.
   *
   * En `false` el blob es el ORIGINAL: puede ser un HEIC de iPhone que el navegador no supo
   * decodificar, un PNG que ya pesaba menos, o un PDF que alguien eligió por error. Quien lo suba
   * NO debe llamarlo `image/jpeg` ni ponerle extensión `.jpg`: quedaría un archivo que el revisor
   * abre y no ve. Antes esto era invisible porque la función solo devolvía un Blob.
   */
  recodificado: boolean;
}

/**
 * Comprime una imagen a JPEG (lado mayor <= maxSide, calidad `quality`) diciendo además si
 * realmente lo consiguió. Objetivo ~<= 350 KB para celulares de gama baja.
 *
 * Si algo falla devuelve el original y `recodificado: false` — nunca rompe el flujo, pero ahora
 * el que llama puede enterarse en vez de suponer.
 */
export async function compressImageResult(
  blob: Blob,
  maxSide = 1600,
  quality = 0.7,
): Promise<CompressResult> {
  try {
    // `imageOrientation: 'from-image'` explícito: sin él, la orientación EXIF depende de la
    // versión del navegador y una foto vertical de celular puede quedar acostada para siempre.
    const bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' });
    const { width, height } = computeTargetSize(bitmap.width, bitmap.height, maxSide);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close();
      return { blob, recodificado: false };
    }
    // Fondo BLANCO antes de dibujar: el canvas nace transparente y el JPEG no tiene canal alfa,
    // así que un PNG con transparencia salía con el fondo en NEGRO. Sobre una foto de documento
    // eso puede tapar el dato que hay que leer.
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();
    const out = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/jpeg', quality),
    );
    if (!out || out.size >= blob.size) return { blob, recodificado: false };
    return { blob: out, recodificado: true };
  } catch (e) {
    // eslint-disable-next-line no-console -- se sube el original: hay que poder saber por qué
    console.warn('compressImage no pudo recodificar; se usa el original', blob.type, blob.size, e);
    return { blob, recodificado: false };
  }
}

/**
 * Igual que `compressImageResult` pero devolviendo solo el blob. Es la forma histórica y la usan
 * los flujos internos (asistencia, canjes, levantamiento), donde el archivo lo elige alguien con
 * sesión y ya viene de la cámara de la app.
 */
export async function compressImage(blob: Blob, maxSide = 1600, quality = 0.7): Promise<Blob> {
  return (await compressImageResult(blob, maxSide, quality)).blob;
}
